// types
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { AppointmentSummary, SyncStatusResponse } from 'generated/index';

// utils
import i18n from 'config/i18n';

import { formatTreatmentDeviations } from 'utils/treatmentDeviation';

import { getLocale } from 'utils/date';
import dayjs from 'dayjs';

const t = i18n.getFixedT(getLocale());

export const DELETED_APPOINTMENT_CLASS = 'deleted-appointment';

export type SyncStatusLogRow = SyncStatusResponse;

export type SyncStatusLogState =
  | 'wont-sync'
  | 'missing-calculation'
  | 'missing-export'
  | 'missing'
  | 'failed-stale'
  | 'stale'
  | 'failed'
  | 'success';

export const getSyncStatusLogState = (
  row: SyncStatusLogRow
): SyncStatusLogState => {
  if (row.wontSync) {
    return 'wont-sync';
  }

  if (row.missingCalculation) {
    return 'missing-calculation';
  }

  if (row.missingExport) {
    return 'missing-export';
  }

  if (row.missing) {
    return 'missing';
  }

  if (row.stale && row.status === 'FAILED') {
    return 'failed-stale';
  }

  if (row.stale) {
    return 'stale';
  }

  if (row.status === 'FAILED') {
    return 'failed';
  }

  return 'success';
};

export const getSyncStatusLogStateLabel = (row: SyncStatusLogRow): string =>
  t(`logsPortal.synchronisationLogs.stateValues.${getSyncStatusLogState(row)}`);

export const getSyncStatusLogErrorMessage = (row: SyncStatusLogRow): string => {
  if (row.wontSync) {
    return (
      row.errorMessage || t('logsPortal.synchronisationLogs.messages.wontSync')
    );
  }

  if (row.missingCalculation) {
    return (
      row.errorMessage ||
      t('logsPortal.synchronisationLogs.messages.missingCalculation')
    );
  }

  if (row.missingExport) {
    return (
      row.errorMessage ||
      t('logsPortal.synchronisationLogs.messages.missingExport')
    );
  }

  if (row.missing) {
    return (
      row.errorMessage || t('logsPortal.synchronisationLogs.messages.missing')
    );
  }

  if (row.stale && row.status !== 'FAILED' && !row.errorMessage) {
    return t('logsPortal.synchronisationLogs.messages.stale');
  }

  return row.errorMessage || '';
};

export const getSyncStatusLogDateTimeLabel = (row: SyncStatusLogRow): string =>
  row.syncDateTime
    ? dayjs(row.syncDateTime).format('DD-MM-YYYY - HH:mm')
    : t('logsPortal.synchronisationLogs.neverSynced');

// noinspection JSUnusedGlobalSymbols
export const getSyncStatusLogRowId = (row: SyncStatusLogRow): string =>
  [
    row.appointmentContext?.appointmentSummary?.id ||
      row.appointmentContext?.appointmentSummary?.startDateTime ||
      'unknown',
    row.destination || 'unknown',
    row.syncDateTime || 'never-synced',
    row.externalId || 'no-external-id',
    getSyncStatusLogState(row)
  ].join('.');

export const getTreatmentDeviationLabel = (row: SyncStatusLogRow): string =>
  formatTreatmentDeviations(row.treatmentDeviations, t);

export const useColumnDefinitions = (): GridColDef<SyncStatusLogRow>[] => [
  {
    field: 'appointmentDate',
    headerName: t('logsPortal.synchronisationLogs.appointmentDate'),
    minWidth: 120,
    sortComparator: (a: dayjs.Dayjs, b: dayjs.Dayjs) => a.diff(b),
    valueGetter: (_, row) =>
      dayjs(row.appointmentContext?.appointmentSummary?.startDateTime),
    valueFormatter: (value: dayjs.Dayjs) => value.format('DD-MM-YYYY'),
    cellClassName: ({ row }) =>
      row.appointmentContext?.appointmentSummary?.deleted
        ? DELETED_APPOINTMENT_CLASS
        : ''
  },
  {
    field: 'appointmentTime',
    headerName: t('logsPortal.synchronisationLogs.appointmentTime'),
    minWidth: 100,
    valueGetter: (_, row) =>
      row.appointmentContext?.appointmentSummary &&
      `${dayjs(row.appointmentContext.appointmentSummary?.startDateTime).format(
        'HH:mm'
      )} - ${dayjs(
        row.appointmentContext.appointmentSummary?.endDateTime
      ).format('HH:mm')}`,
    cellClassName: ({ row }) =>
      row.appointmentContext?.appointmentSummary?.deleted
        ? DELETED_APPOINTMENT_CLASS
        : ''
  },
  {
    field: 'activity',
    headerName: t('logsPortal.synchronisationLogs.activity'),
    minWidth: 200,
    valueGetter: (_, row) => {
      const name =
        row.appointmentContext?.appointmentSummary?.activity?.name || '';
      const code = row.appointmentContext?.appointmentSummary?.activity?.code;
      return code ? `${name} (${code})` : name;
    },
    cellClassName: ({ row }) =>
      row.appointmentContext?.appointmentSummary?.deleted
        ? DELETED_APPOINTMENT_CLASS
        : ''
  },
  {
    field: 'participants',
    headerName: t('logsPortal.synchronisationLogs.participants'),
    minWidth: 300,
    flex: 1,
    valueGetter: (_, row) =>
      row.appointmentContext?.appointmentSummary &&
      getParticipantPreview(row.appointmentContext.appointmentSummary),
    cellClassName: ({ row }) =>
      row.appointmentContext?.appointmentSummary?.deleted
        ? DELETED_APPOINTMENT_CLASS
        : ''
  },
  {
    field: 'state',
    headerName: t('logsPortal.synchronisationLogs.state'),
    minWidth: 140,
    valueGetter: (_, row) => getSyncStatusLogStateLabel(row)
  },
  {
    field: 'destination',
    headerName: t('logsPortal.synchronisationLogs.destination'),
    minWidth: 100,
    valueGetter: (_, row) => row.destination || null
  },
  {
    field: 'treatmentDeviation',
    headerName: t('logsPortal.synchronisationLogs.treatmentDeviation'),
    minWidth: 260,
    valueGetter: (_, row) => getTreatmentDeviationLabel(row) || null
  },
  {
    field: 'errorCode',
    headerName: t('logsPortal.synchronisationLogs.errorCode'),
    minWidth: 150,
    valueGetter: (_, row) => row.errorCode || null
  },
  {
    field: 'error',
    headerName: t('logsPortal.synchronisationLogs.error'),
    flex: 1,
    minWidth: 400,
    valueGetter: (_, row) => getSyncStatusLogErrorMessage(row)
  },
  {
    field: 'syncDateTime',
    headerName: t('logsPortal.synchronisationLogs.syncDateTime'),
    minWidth: 170,
    valueGetter: (_, row) => row.syncDateTime || null,
    valueFormatter: (_, row) => getSyncStatusLogDateTimeLabel(row)
  }
];

export const getParticipantPreview = (
  appointmentSummary: AppointmentSummary
) => {
  const MAX_VISIBLE_PARTICIPANTS = 3;
  const participants = [
    ...(appointmentSummary?.clients || []),
    ...(appointmentSummary?.employees || [])
  ];

  if (participants.length > MAX_VISIBLE_PARTICIPANTS) {
    const visibleParticipants = participants.slice(0, MAX_VISIBLE_PARTICIPANTS);
    const remainingCount = participants.length - MAX_VISIBLE_PARTICIPANTS;
    return `${visibleParticipants
      .map(({ displayName }) => displayName)
      .join(', ')} ${t(
      'logsPortal.synchronisationLogs.additionalParticipants',
      { count: remainingCount }
    )}`;
  }

  return participants.map(({ displayName }) => displayName).join(', ') || '';
};
